<template lang="pug">
  AppModal(:value="showModal" @input="onClose" :title="'Historial de modificaciones — ' + (packageCode || '')")
    div(v-if="loading" class="history-loading")
      span.history-spinner
      span Cargando historial...
    template(v-else-if="errorMsg")
      p.history-error {{ errorMsg }}
    template(v-else-if="history.length === 0")
      p Sin modificaciones registradas.
    template(v-else)
      .history-entry(v-for="(entry, index) in history" :key="entry.id")
        .d-flex.justify-content-between.align-items-center
          div
            AppIcon(icon="edit" :size="18" color="var(--info)")
            span.entry-date.ml-1 {{ formatDateTime(entry.changedAt) }}
          span.history-badge(:class="'history-badge--' + (entry.action || 'update')") {{ actionLabel(entry.action) }}
        div.subtitle.mt-1
          span Modificado por&nbsp;
          strong {{ entry.changedByUser ? entry.changedByUser.name + ' ' + entry.changedByUser.lastName : 'Sistema' }}
          span(v-if="entry.changedByUser")  ({{ roleLabel(entry.changedByUser.role) }})
        ul.changes-list.mt-2.mb-0
          li(v-for="change in entry.changes" :key="change.field")
            strong {{ change.label }}:&nbsp;
            span.change-old(v-if="hasValue(change.oldValue)") {{ formatValue(change.field, change.oldValue) }}
            span.change-sep(v-if="hasValue(change.oldValue)") &nbsp;→&nbsp;
            span.change-new {{ hasValue(change.newValue) ? formatValue(change.field, change.newValue) : '—' }}
        hr(v-if="index < history.length - 1")
</template>

<script>
import moment from 'moment-timezone'
import AppModal from '@/components/UI/AppModal.vue'
import AppIcon from '@/components/UI/AppIcon.vue'

const ACTION_LABELS = {
  update: 'Edición',
  cancel: 'Cancelación',
  uncancel: 'Reactivación',
  reception: 'Recepción en bodega',
  pickup: 'Retiro por conductor',
  'in-transit': 'En tránsito',
  'exchange-or-return': 'Cambio o devolución',
  'label-print': 'Impresión de etiqueta'
}

const ROLE_LABELS = {
  admin: 'Administrador',
  store: 'Tienda',
  viewer: 'Visor',
  driver: 'Conductor'
}

export default {
  name: 'PackageHistoryModal',
  components: {
    AppModal,
    AppIcon
  },
  props: {
    packageItem: { type: Object, default: null }
  },
  data() {
    return {
      showModal: false,
      loading: false,
      errorMsg: '',
      history: [],
      packageId: null,
      packageCode: null,
      loadSeq: 0
    }
  },
  methods: {
    async show(packageItem) {
      const item = packageItem || this.packageItem
      const seq = ++this.loadSeq
      this.packageId = item && item.id
      this.packageCode = item && item.code
      this.history = []
      this.errorMsg = ''
      this.loading = true
      this.showModal = true
      try {
        const res = await this.$store.dispatch('getPackageHistory', { id: this.packageId })
        if (seq !== this.loadSeq) return
        this.history = (res && res.data ? res.data : res) || []
      } catch (error) {
        console.error(error)
        if (seq !== this.loadSeq) return
        this.errorMsg = 'No se pudo cargar el historial del paquete'
      } finally {
        if (seq === this.loadSeq) {
          this.loading = false
        }
      }
    },
    onClose() {
      this.showModal = false
    },
    formatDateTime(value) {
      if (!value) return '—'
      return moment(value).tz('America/Santiago').format('DD-MM-YYYY HH:mm A')
    },
    actionLabel(action) {
      return ACTION_LABELS[action] || action || 'Edición'
    },
    roleLabel(role) {
      return ROLE_LABELS[role] || role
    },
    hasValue(value) {
      return value !== null && value !== undefined && value !== ''
    },
    formatValue(field, value) {
      if (value === null || value === undefined || value === '') return '—'
      if (typeof value === 'boolean') return value ? 'Si' : 'No'
      if (field === 'shippingCost') return `$${Number(value).toLocaleString('es-CL')}`
      if (field === 'size') {
        const sizes = { small: 'PEQUEÑO', medium: 'MEDIANO' }
        return sizes[value] || value
      }
      if (field === 'tags') {
        const tags = { flex: 'Flex', exchange: 'Cambio', return: 'Devolución' }
        return tags[value] || value
      }
      if (field === 'receptionStatus') return value === 'received' ? 'Recibido' : value
      return String(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.history-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 2rem 0;
  color: #6b7280;
}

.history-spinner {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(0, 0, 0, 0.15);
  border-top-color: var(--app-primary, #0f2835);
  border-radius: 50%;
  animation: history-spin 0.6s linear infinite;
}

.history-error {
  color: var(--app-danger, #f46a6a);
}

@keyframes history-spin {
  to {
    transform: rotate(360deg);
  }
}

.entry-date {
  font-weight: 600;
  color: #374151;
}

.subtitle {
  font-size: 13px;
  color: #6c757d;
}

.history-badge {
  display: inline-block;
  padding: 0.25rem 0.6rem;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 999px;
  background: #eef2ff;
  color: #3730a3;
}

.history-badge--cancel {
  background: #fee2e2;
  color: #b91c1c;
}

.history-badge--reception,
.history-badge--label-print,
.history-badge--uncancel {
  background: #dcfce7;
  color: #15803d;
}

.history-badge--pickup,
.history-badge--in-transit {
  background: #dbeafe;
  color: #1d4ed8;
}

.history-badge--exchange-or-return {
  background: #fef3c7;
  color: #b45309;
}

.changes-list {
  padding-left: 1.25rem;
  li {
    font-size: 13px;
    margin-bottom: 2px;
  }
}

.change-old {
  color: var(--status-rejected, #f46a6a);
  text-decoration: line-through;
}

.change-new {
  color: var(--success, #34c38f);
  font-weight: 500;
}

.change-sep {
  color: #6c757d;
}
</style>
